import i18n from 'i18next';
import { FORM_VALUES, TIME_FORMAT } from './constants';
import {
  getFloorError,
  getImagesError,
  getNumberError,
  getPriceError,
  getRoomsCountError,
  validateFloor,
  validateNumber,
  validatePhotos,
  validatePrice,
  validateRoomsCount
} from './validateItemForm';
import {
  checkIsFlatCategory,
  formatToBakuTime,
  isNotNil,
  ValidatorsConfigEntry
} from './common';
import { UpdateItemMutationVariables } from '../../graphql/graphql';
import { CategoryValues, Photo } from './convertFormToQueryInput';

export const getLockedAlert = (
  lockedUntil: string | number | Date | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
) => {
  const errorPath = 'pages.current_item.alert_modal.locked_edit_request';
  const { numDateTime } = TIME_FORMAT;
  const timestamp = formatToBakuTime(lockedUntil, i18n, numDateTime);

  return {
    info: t(errorPath, { timestamp }),
    button: t('pages.current_item.buttons.accepted'),
    title: t('pages.current_item.attention')
  };
};

export type ItemEditFormValuesType = {
  area: string | number;
  name: string;
  description: string;
  itemId: string;
  photos: Photo[];
  price: string | number;
  landArea?: string | number | null;
  floor?: string | number | null;
  totalFloors?: string | number | null;
  hasBillOfSale?: boolean | null;
  hasMortgage?: boolean | null;
  hasRepair?: boolean | string | null;
  paidDaily?: boolean | string | null;
  roomsAmount?: string | number | null;
  categoryValues: CategoryValues;
  isLeased: boolean | string;
  categoryId: string;
  locationEditable?: boolean;
  cityHasLocations?: boolean;
  address?: string;
  city_id?: string;
  coordinates?: { lat?: number | null; lng?: number | null };
  regionId?: string | null;
  townshipId?: string | null;
  landmarkIds?: string[] | null;
};

export const convertEditValues = (
  values: ItemEditFormValuesType
): UpdateItemMutationVariables => {
  const res: UpdateItemMutationVariables = {
    area: {
      value: Number(values.area)
    },
    contactName: values.name,
    description: values.description,
    itemId: values.itemId,
    photoIds: values.photos.map(el => el.id),
    price: Number(values.price)
  };

  if (values[FORM_VALUES.locationEditable]) {
    res.location = {
      address: values.address!,
      cityId: values.city_id!,
      latitude: values.coordinates?.lat!,
      longitude: values.coordinates?.lng!,
      regionId: values.regionId,
      townshipId: values.townshipId,
      landmarkIds: values.landmarkIds
    };
  }

  if (isNotNil(values.landArea)) {
    res.area!.land = Number(values.landArea);
  }
  if (isNotNil(values.floor) && isNotNil(values.totalFloors)) {
    res.floor = {
      value: Number(values.floor),
      total: Number(values.totalFloors)
    };
  }
  if (isNotNil(values.hasBillOfSale)) {
    res.hasBillOfSale = values.hasBillOfSale;
  }
  if (isNotNil(values.hasMortgage)) {
    res.hasMortgage = values.hasMortgage;
  }
  if (isNotNil(values.hasRepair)) {
    res.hasRepair =
      typeof values.hasRepair === 'boolean'
        ? values.hasRepair
        : values.hasRepair === 'true';
  } else {
    res.hasRepair = null;
  }
  if (isNotNil(values.paidDaily)) {
    res.paidDaily =
      typeof values.paidDaily === 'boolean'
        ? values.paidDaily
        : values.paidDaily === 'true';
  }
  if (isNotNil(values.roomsAmount)) {
    res.rooms = Number(values.roomsAmount);
  }

  return res;
};

export const validatorsConfig: Record<
  string,
  ValidatorsConfigEntry<ItemEditFormValuesType>
> = {
  [FORM_VALUES.locationId]: {
    required: values =>
      !!(
        values[FORM_VALUES.locationEditable] &&
        values[FORM_VALUES.cityHasLocations]
      ),
    errorMessage: 'new_ad.required'
  },
  [FORM_VALUES.cityId]: {
    required: values => !!values[FORM_VALUES.locationEditable],
    errorMessage: 'new_ad.required'
  },
  [FORM_VALUES.address]: {
    required: values => !!values[FORM_VALUES.locationEditable],
    errorMessage: 'new_ad.required'
  },
  [FORM_VALUES.coordinates]: {
    required: values => !!values[FORM_VALUES.locationEditable],
    errorMessage: 'new_ad.required'
  },
  [FORM_VALUES.paidDaily]: {
    required: values => values[FORM_VALUES.leased] === 'true',
    errorMessage: 'new_ad.required'
  },
  [FORM_VALUES.roomsAmount]: {
    required: values =>
      values[FORM_VALUES.categoryValues].hasRooms &&
      values[FORM_VALUES.categoryValues].areRoomsRequired,
    validator: validateRoomsCount,
    errorMessage: getRoomsCountError(FORM_VALUES.roomsAmount)
  },
  [FORM_VALUES.area]: {
    required: true,
    validator: validateNumber,
    errorMessage: getNumberError(FORM_VALUES.area)
  },
  [FORM_VALUES.landArea]: {
    required: values => values[FORM_VALUES.categoryValues].hasLandArea,
    validator: validateNumber,
    errorMessage: getNumberError(FORM_VALUES.landArea)
  },
  [FORM_VALUES.floor]: {
    required: values =>
      values[FORM_VALUES.categoryValues].hasFloor ||
      checkIsFlatCategory(values[FORM_VALUES.categoryId]),
    validator: validateFloor,
    errorMessage: getFloorError
  },
  [FORM_VALUES.totalFloors]: {
    required: values =>
      values[FORM_VALUES.categoryValues].hasFloor ||
      checkIsFlatCategory(values[FORM_VALUES.categoryId]),
    validator: validateNumber,
    errorMessage: getNumberError(FORM_VALUES.totalFloors, true)
  },
  [FORM_VALUES.price]: {
    required: true,
    validator: validatePrice,
    errorMessage: getPriceError
  },
  [FORM_VALUES.name]: {
    required: true,
    errorMessage: 'new_ad.required'
  },
  [FORM_VALUES.images]: {
    required: true,
    validator: validatePhotos,
    errorMessage: getImagesError
  }
};
